/**
 * Shared wire-format types for the Building_Template_Editor.
 *
 * These mirror the JSON schema in
 * `.kiro/specs/building-template-editor/requirements.md` → Data Model.
 * They are consumed by:
 *   - The editor's draft reducer and API client (admin surface).
 *   - The runtime `TemplateRegistry` and `BuildingSprite` (world surface).
 *
 * All slot coordinates and the footprint baseline are expressed in
 * **Original_Image pixel space**. The Published_Image is a pure derivative
 * of the Original_Image via optional crop and a uniform scale derived from
 * the footprint baseline — see `templateTransform.ts`.
 *
 * Pure types only. No runtime code, no React, no DOM.
 */

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface ImageRecord {
  readonly s3_key: string;
  readonly width: number;
  readonly height: number;
  readonly sha256: string;
}

export type PublishedImageSource = "original" | "background_removed";

export interface PublishedImageRecord {
  readonly s3_key: string;
  readonly width: number;
  readonly height: number;
  readonly sha256: string;
  readonly crop: Rect | null;
  readonly source: PublishedImageSource;
  /** The same image as WebP (migration 178), at `.../published.webp`. Absent until the
   *  template has one; the PNG stays the fallback. */
  readonly webp_s3_key?: string | null;
}

export interface FootprintBaseline {
  readonly left: Point;
  readonly right: Point;
  /** How many cells the art spans along its edge (a land threshold); absent: one. */
  readonly cells?: number;
}

export interface PointSlot {
  readonly shape: "point";
  readonly x: number;
  readonly y: number;
}

/**
 * Which isometric face a slot is placed on.
 * - "left": the face going from upper-left to lower-right (skews /)
 * - "right": the face going from upper-right to lower-left (skews \)
 * - "front": flat/centered (no skew — used for roof-mounted elements)
 */
export type IsoFace = "left" | "right" | "front";

export interface RectSlot {
  readonly shape: "rect";
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** Which isometric face this slot sits on. Determines skew transform. */
  readonly face?: IsoFace;
}

/**
 * QuadSlot — 4 corner points defining a parallelogram region.
 * Used when the admin draws a skewed region in the building editor
 * that matches the isometric perspective exactly.
 */
export interface QuadSlot {
  readonly shape: "quad";
  readonly topLeft: Point;
  readonly topRight: Point;
  readonly bottomLeft: Point;
  readonly bottomRight: Point;
  readonly face?: IsoFace;
}

export type BannerStripDirection = "row" | "column";

export interface BannerStripSlot {
  readonly shape: "banner_strip";
  readonly anchor: Point;
  readonly direction: BannerStripDirection;
  readonly banner_size: { readonly width: number; readonly height: number };
  readonly spacing: number;
  readonly max_count: number;
  /** Which isometric face this strip sits on. */
  readonly face?: IsoFace;
}

export type ProjectTypeHint =
  | "game-looking"
  | "story-looking"
  | "art-looking"
  | "app-looking"
  | "song-looking"
  | "any";

export const PROJECT_TYPE_HINTS: readonly ProjectTypeHint[] = [
  "game-looking",
  "story-looking",
  "art-looking",
  "app-looking",
  "song-looking",
  "any",
];

/**
 * What a template is. `landmark` is a Land's named place (Lookout, Hive
 * Hill, the town gate) — neither a project nor a system building
 * (WORLD_MODEL.md §6). Mirrors `BuildingKind` in
 * `backend/app/schemas/building_templates.py`.
 */
export type BuildingKind = "project" | "system" | "covered" | "ground" | "landmark";

export const BUILDING_KINDS: readonly BuildingKind[] = [
  "project",
  "system",
  "covered",
  "ground",
  "landmark",
];

/**
 * Ground tile variants. The first five are the town's original set; `water`,
 * `stepping_stones` and `edge` came with the Lands world model
 * (WORLD_MODEL.md §3). Mirrors `GROUND_VARIANTS` on the backend.
 */
export type GroundVariant =
  | "walkway_intersection"
  | "walkway_down_left"
  | "walkway_down_right"
  | "empty_lot"
  | "grass"
  | "water"
  | "stepping_stones"
  | "edge"
  // Phase 1d catalogue kinds (checklist 2.27): the water class, the Mountain's ground,
  // the Sky's cloud, and the per-land boundary kinds that take the hedge's place on a ring.
  | "water_open"
  | "deep"
  | "gap"
  | "snow"
  | "rock"
  | "floor"
  | "cloud"
  | "dune"
  | "quay"
  | "cloud_rim"
  | "flags"
  // Ring cells with a one-off look (2026-10-23): the Clouds' still air above its top ring,
  // and the tiny garden far below Storm and Stars' front ring. Both fall back to the kind
  // they replace while their art is unpublished (GROUND_VARIANT_FALLBACK).
  | "calm"
  | "gap_garden"
  // Space (08-space.md §3): the Spaceport's apron, the Moon's dust, Mars's red dune
  // (`dune` is the Sea's boundary band, so walkable red ground is its own kind).
  | "apron"
  | "dust"
  | "red_dust"
  // The Sea's pier (04-sea.md §3.2): planks over open water. Walkable, so not in the
  // water class, but a masked BAND rather than a crossing — a pier runs a whole row and
  // its cells differ, where every `stepping_stones` cell looks the same.
  | "pier";

export const GROUND_VARIANTS: readonly GroundVariant[] = [
  "walkway_intersection",
  "walkway_down_left",
  "walkway_down_right",
  "empty_lot",
  "grass",
  "water",
  "stepping_stones",
  "edge",
  "water_open",
  "deep",
  "gap",
  "snow",
  "rock",
  "floor",
  "cloud",
  "dune",
  "quay",
  "cloud_rim",
  "flags",
  "calm",
  "gap_garden",
  "apron",
  "dust",
  "red_dust",
  // The Sea's pier: planks over open water, walkable, and a MASKED band rather than a
  // crossing. `stepping_stones` is one tile because every stone cell looks the same; a
  // pier runs a whole row and its cells differ, so it needs all eleven arm masks
  // (`04-sea.md` §3.2, art lane 2026-10-16).
  "pier",
];

/** The water class: blocks, and the renderer's gap logic treats it as water. */
export const WATER_LIKE_GROUND_VARIANTS: ReadonlySet<string> = new Set(["water", "water_open", "deep", "gap"]);
/** Boundary kinds: a zone's ring, never walkable, one continuous band like the hedge. */
export const BOUNDARY_GROUND_VARIANTS: ReadonlySet<string> = new Set(["edge", "dune", "quay", "cloud_rim", "flags", "calm", "gap_garden"]);
/** A one-off ring kind draws as the kind it replaces until its own art is published. */
export const GROUND_VARIANT_FALLBACK: Readonly<Partial<Record<GroundVariant, GroundVariant>>> = { calm: "cloud_rim", gap_garden: "gap" };
/** Ground variants the avatar can never stand on, whatever the admin ticks. */
export const NON_WALKABLE_GROUND_VARIANTS: ReadonlySet<string> = new Set([...WATER_LIKE_GROUND_VARIANTS, ...BOUNDARY_GROUND_VARIANTS]);

/**
 * `WORLD_MODEL.md` v3 §2 — the eleven arm masks a valid layout produces. A band cell is
 * addressed as `<base>_<mask>`, so the catalogue key for a path cell connecting upper-left
 * and lower-right is `walkway_ul_lr`. Mirrors `GROUND_ARM_MASKS` on the backend.
 */
export const GROUND_ARM_MASKS: ReadonlySet<string> = new Set([
  "ul_lr", "ur_ll",
  "ul_ur", "ur_lr", "lr_ll", "ul_ll",
  "ul_ur_lr", "ur_lr_ll", "ul_lr_ll", "ul_ur_ll",
  "ul_ur_lr_ll",
]);

/**
 * Which kinds draw a band and therefore take a mask (a full fill does not). A crossing takes
 * one too since 2026-10-29: the Clouds' stepping stones run the other way from the Meadow's,
 * and one baked tile could not draw both, so a crossing is keyed by the arms of its STONES —
 * the line a kid walks — exactly like a walkway. The water beside them is a separate field
 * (`GroundCellSpec.waterArms`) and never reaches this key.
 *
 * Flipping a kind into this table makes the renderer ask for `<base>_<mask>` instead of the
 * bare variant, so the art for every mask that kind can produce must already be PUBLISHED —
 * on each environment separately — or the cell draws a placeholder. See
 * `tools/check_ground_templates_published.py`.
 */
const MASKABLE_BASES: Record<string, string> = {
  walkway_intersection: "walkway",
  walkway_down_left: "walkway",
  walkway_down_right: "walkway",
  water: "water",
  deep: "deep",
  gap: "gap",
  pier: "pier",
  stepping_stones: "stepping_stones",
};

/**
 * The catalogue key for a ground cell: `<base>_<mask>` where the cell carries a mask and
 * its kind draws a band, else the variant itself. One place so the renderer, the admin
 * editor and the delivery runbook cannot disagree about the spelling.
 */
export function groundTileKey(variant: string, arms?: string): string {
  const base = MASKABLE_BASES[variant];
  if (!base || !arms || !GROUND_ARM_MASKS.has(arms)) return variant;
  return `${base}_${arms}`;
}

/** Bases a door stub may sit on: plain ground, never a walkway or a band kind. */
const STUB_BASES: ReadonlySet<string> = new Set([
  "grass", "empty_lot", "rock", "floor", "snow", "cloud", "apron", "dust", "red_dust",
]);

/**
 * Is this a key the catalogue could hold? A base variant, a masked band, or a stub on a
 * base that can carry one. Mirrors `is_valid_ground_variant` on the backend, which is the
 * authority — this twin exists so a test can prove the renderer never asks for a key the
 * create route would reject.
 */
/**
 * Bases whose masked form `<base>_<mask>` is a key the create route accepts. The twin of
 * `MASKABLE_GROUND_BASES` in `backend/app/schemas/building_templates.py`, which is the authority;
 * `groundVariantTwins.test.ts` reads that file and proves the two agree. They had drifted: `pier`
 * was on the backend and not here, harmless only because no pier cell carries arms today, so the
 * renderer never asks for `pier_*` and the guard below never saw one.
 */
export const MASKABLE_GROUND_BASES: readonly string[] = ["walkway", "water", "deep", "gap", "pier", "stepping_stones"];

export function isValidGroundVariant(value: string): boolean {
  if ((GROUND_VARIANTS as readonly string[]).includes(value)) return true;
  for (const base of MASKABLE_GROUND_BASES) {
    if (value.startsWith(`${base}_`) && GROUND_ARM_MASKS.has(value.slice(base.length + 1))) return true;
  }
  for (const base of STUB_BASES) {
    const rest = value.startsWith(`${base}_`) ? value.slice(base.length + 1) : null;
    if (rest === "stub_lr" || rest === "stub_ll") return true;
  }
  return false;
}

/** Every existing template belongs to the town until it says otherwise. */
export const DEFAULT_ZONE = "town";

export interface LineSlot {
  readonly shape: "line";
  readonly start: Point;
  readonly end: Point;
}

export interface BuildingTemplateSlots {
  /**
   * Where a child goes in. Present only for a kind with an inside (project / system /
   * covered); a ground tile or a landmark — a crater, an island, the big tree — has none,
   * and inventing one to satisfy the shape would be a lie in the data.
   */
  readonly door?: PointSlot | null;
  /** Door line — full bottom edge of doorway. When present, overrides door point for collision. */
  readonly door_line?: LineSlot | null;
  /** Optional — not all buildings have an ornament slot. */
  readonly ornament?: RectSlot | QuadSlot | null;
  /** Optional — not all buildings have a preview window. */
  readonly preview_window?: RectSlot | QuadSlot | null;
  /** Optional — not all buildings have a sign slot. */
  readonly sign?: BannerStripSlot | null;
  /** Optional — not all buildings have classification banners. */
  readonly classification_banners?: BannerStripSlot | null;
}

export interface BuildingTemplate {
  readonly id?: string;
  readonly name: string;
  readonly version: number;
  readonly project_type_hint?: ProjectTypeHint;
  readonly description?: string;
  readonly building_kind?: BuildingKind;
  readonly entrance_side?: "left" | "right";
  readonly ground_variant?: string | null;
  readonly weight?: number;
  /** Which Land's set this template belongs to. Absent = `town`. */
  readonly zone?: string;
  /** Ground only: can the avatar stand on this cell? Absent = `true`. */
  readonly walkable?: boolean;
  /** Non-ground only: may the avatar walk onto this occupant (exits)? Absent = `false`. */
  readonly walkable_occupant?: boolean;
  readonly system_ref?: string | null;
  readonly original_image: ImageRecord;
  readonly background_removed_image?: ImageRecord | null;
  readonly published_image: PublishedImageRecord;
  readonly published_at?: string | null;
  readonly footprint_baseline: FootprintBaseline;
  readonly slots: BuildingTemplateSlots;
}
